import { z } from "zod";
import { connectorProviderSchema } from "./connectors.js";
import { inboxActionIdSchema, inboxConversationKey, inboxItemStateSchema, inboxSourceActionSchema, inboxSourceLabel, } from "./inbox.js";
/** A page of the person's incoming items: what needs them (`active`) or what they've dealt with (`done`). */
export const twinInboxListQuerySchema = z.object({
    bucket: z.enum(["active", "done"]).default("active"),
    source: connectorProviderSchema.optional(),
    /** One source left out. */
    excludeSource: connectorProviderSchema.optional(),
    /**
     * One source left out where the items are the person's own connection's, keeping a shared mailbox's: for a client
     * that is already that source's client for the person's own account (Desk, of Gmail).
     */
    excludeOwnSource: connectorProviderSchema.optional().describe("Leaves out this source where the items are the caller's own connection's, keeping a shared mailbox's."),
    cursor: z.string().trim().min(1).max(2048).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
}).strict();
/** One item, narrowed for clients outside the app: no body, raw payload or storage keys. */
export const twinInboxItemSchema = z.object({
    id: z.string().trim().min(1),
    source: connectorProviderSchema,
    /** The source's name in words ("Microsoft Outlook"), as the web Inbox names it. */
    sourceLabel: z.string().trim().min(1).describe("The source's name in words (Microsoft Outlook), as the web Inbox names it."),
    entity: z.string().trim().min(1),
    sender: z.string(),
    preview: z.string(),
    /** Where it sits at its source: a repository, a subject, a channel. */
    sourceContext: z.string().describe("Where it sits at its source: a repository, a subject, a channel."),
    /** The conversation it belongs to (an email thread, a Slack channel): the web Inbox lists each as one row. */
    conversationKey: z.string().trim().min(1).describe("The conversation it belongs to (an email thread, a Slack channel): the web Inbox lists each as one row."),
    receivedAt: z.number().int().nonnegative(),
    state: inboxItemStateSchema,
    mentioned: z.boolean(),
    sourceUrl: z.string().trim().min(1).nullable(),
    /** What Done and Undo do at the source, as the server advertises it for this item. */
    availableActions: z.array(inboxSourceActionSchema),
    /**
     * The shared mailbox it came in to (info@), which the person reads in the Twin; null for their own items and the
     * organisation's. A client that reads that address itself, as one of the person's own accounts, leaves it out, so
     * the mailbox never shows twice (review 18 Oct, finding 5).
     */
    sharedMailboxAddress: z.string().trim().min(1).nullable().default(null).describe("The shared mailbox it came in to (info@), which the caller reads in the Twin; null for their own items and the organisation's. A client that reads that address itself leaves it out."),
}).strict().meta({ title: "TwinInboxItem" });
export const twinInboxListResponseSchema = z.object({
    items: z.array(twinInboxItemSchema),
    nextCursor: z.string().nullable(),
    hasMore: z.boolean(),
}).strict().meta({ title: "TwinInboxListResponse" });
/** Done or back in the Inbox; linking an item to a channel or record stays the app's. */
export const twinInboxUpdateInputSchema = z.object({ state: z.enum(["new", "dismissed"]) }).strict().meta({ title: "TwinInboxUpdateInput" });
export const twinInboxItemResponseSchema = z.object({ item: twinInboxItemSchema }).strict().meta({ title: "TwinInboxItemResponse" });
/** One of the item's advertised actions, run at its source (archive the email in Outlook, say). */
export const twinInboxActionInputSchema = z.object({ actionId: inboxActionIdSchema }).strict().meta({ title: "TwinInboxActionInput" });
export const twinInboxActionResponseSchema = z.object({ actionId: inboxActionIdSchema, item: twinInboxItemSchema }).strict().meta({ title: "TwinInboxActionResponse" });
/** The app's inbox item, as clients outside the app see it. */
export function twinInboxItem(item) {
    return {
        id: item.id,
        source: item.source,
        sourceLabel: inboxSourceLabel(item.source),
        entity: item.entity,
        sender: item.sender,
        preview: item.preview,
        sourceContext: item.sourceContext,
        conversationKey: inboxConversationKey(item),
        receivedAt: item.receivedAt,
        state: item.state,
        mentioned: item.mentioned,
        sourceUrl: item.sourceUrl,
        availableActions: item.availableActions,
        sharedMailboxAddress: item.sharedMailboxAddress,
    };
}
export const twinInboxPathParamsSchema = z.object({ inboxItemId: twinInboxItemSchema.shape.id }).strict();
